// frontend/src/pages/admin/EditBlogPostPage.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Globe, Upload, X, Calendar, User, Clock } from 'lucide-react';
import { SEOEditor } from '../../components/SEO/SEOEditor';
import { PageSEO } from '../../components/SEO/PageSEO';
import config from '../../config';

// تابع تبدیل متن به slug (حفظ حروف فارسی)
const slugify = (text) => {
  if (!text) return '';

  let slug = text
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^\w\u0600-\u06FF\-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  if (!slug) {
    slug = 'post-' + Date.now();
  }
  return slug;
};

export const EditBlogPostPage = () => {
  const { slug: urlSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSEOPanel, setShowSEOPanel] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [manualSlug, setManualSlug] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    page_type: 'blog',
    author: '',
    read_time: '',
  });
  const [featuredImage, setFeaturedImage] = useState(null);
  const [seoData, setSeoData] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    focus_keyword: '',
  });

  // ✅ اضافه کردن isNew
  const isNew = urlSlug === 'new' || !urlSlug;

  useEffect(() => {
    if (!isNew && urlSlug && urlSlug !== 'new') {
      fetchPost();
    } else {
      setLoading(false);
    }
  }, [urlSlug, isNew]);

  const fetchPost = async () => {
    try {
      const response = await fetch(config.getApiUrl(`/cms/blog/${encodeURIComponent(urlSlug)}/`));
      if (!response.ok) throw new Error('پست یافت نشد');
      const data = await response.json();
      setFormData({
        title: data.title,
        slug: data.slug,
        excerpt: data.excerpt || '',
        content: data.content || '',
        page_type: data.page_type || 'blog',
        author: data.author?.id || '',
        read_time: data.read_time || '',
      });
      setManualSlug(true);
      setFeaturedImage(data.featured_image);
      setSeoData({
        meta_title: data.meta_title || data.title,
        meta_description: data.meta_description || data.excerpt || data.content?.substring(0, 160) || '',
        meta_keywords: data.meta_keywords || '',
        focus_keyword: data.focus_keyword || '',
      });
    } catch (error) {
      console.error('Error fetching post:', error);
      alert('خطا در بارگذاری اطلاعات');
      navigate('/admin/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'title') {
      setFormData(prev => ({ ...prev, title: value }));
      if (!manualSlug) {
        const generatedSlug = slugify(value);
        setFormData(prev => ({ ...prev, slug: generatedSlug }));
      }
    } else if (name === 'slug') {
      let cleanSlug = value
        .trim()
        .replace(/[\s_]+/g, '-')
        .replace(/[^\w\u0600-\u06FF\-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      setFormData(prev => ({ ...prev, slug: cleanSlug }));
      setManualSlug(true);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveSEO = async (newSeoData) => {
    setSeoData(newSeoData);
    setShowSEOPanel(false);
    alert('تنظیمات سئو ذخیره شد (برای ذخیره نهایی، فرم را ارسال کنید)');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('لطفاً فقط فایل تصویر انتخاب کنید');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('حجم تصویر نباید بیشتر از 2 مگابایت باشد');
      return;
    }

    const accessToken = localStorage.getItem('access_token');
    const formDataImg = new FormData();
    formDataImg.append('featured_image', file);

    setUploadingImage(true);
    try {
      const encodedSlug = encodeURIComponent(urlSlug);
      const response = await fetch(config.getApiUrl(`/cms/blog/${encodedSlug}/upload_image/`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: formDataImg
      });
      if (response.ok) {
        const data = await response.json();
        setFeaturedImage(data.featured_image);
        alert('تصویر شاخص با موفقیت آپلود شد');
      } else {
        alert('خطا در آپلود تصویر');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('خطا در ارتباط با سرور');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let finalSlug = formData.slug;
    if (!finalSlug || finalSlug.trim() === '') {
      finalSlug = slugify(formData.title);
    }

    if (!finalSlug || finalSlug.trim() === '') {
      alert('آدرس یکتا (slug) نمی‌تواند خالی باشد');
      return;
    }

    const invalidChars = finalSlug.match(/[^\w\u0600-\u06FF\-]/g);
    if (invalidChars) {
      alert(`آدرس یکتا حاوی کاراکترهای غیرمجاز است: ${invalidChars.join(', ')}`);
      return;
    }

    const accessToken = localStorage.getItem('access_token');
    setSubmitting(true);

    try {
      const updateData = {
        title: formData.title,
        slug: finalSlug,
        excerpt: formData.excerpt,
        content: formData.content,
        page_type: formData.page_type,
        read_time: formData.read_time || Math.ceil(formData.content.split(/\s+/).length / 200),
        meta_title: seoData.meta_title,
        meta_description: seoData.meta_description,
        meta_keywords: seoData.meta_keywords,
        focus_keyword: seoData.focus_keyword,
      };

      let response;
      if (isNew) {
        // ایجاد مقاله جدید
        response = await fetch(config.getApiUrl('/cms/blog/'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify(updateData)
        });
      } else {
        // ویرایش مقاله موجود
        const encodedSlug = encodeURIComponent(urlSlug);
        response = await fetch(config.getApiUrl(`/cms/blog/${encodedSlug}/`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify(updateData)
        });
      }

      if (response.ok) {
        alert(isNew ? 'مقاله با موفقیت ایجاد شد' : 'مقاله با موفقیت ویرایش شد');
        navigate('/admin/blog');
      } else {
        const error = await response.json();
        alert('خطا: ' + JSON.stringify(error));
      }
    } catch (error) {
      console.error('Error saving post:', error);
      alert('خطا در ارتباط با سرور');
    } finally {
      setSubmitting(false);
    }
  };

  const getKeywordSuggestions = () => {
    const words = formData.content?.split(/\s+/) || [];
    const wordCount = {};
    const stopWords = ['این', 'آن', 'که', 'به', 'از', 'برای', 'با', 'را', 'تا', 'و'];
    words.forEach(word => {
      const cleanWord = word.replace(/[،،.!؟;:]/g, '').toLowerCase();
      if (cleanWord.length > 3 && !stopWords.includes(cleanWord)) {
        wordCount[cleanWord] = (wordCount[cleanWord] || 0) + 1;
      }
    });
    return Object.entries(wordCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([word]) => word);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO title={`${isNew ? 'افزودن' : 'ویرایش'} مقاله | پنل ادمین`} noindex={true} />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">
                {isNew ? 'افزودن مقاله جدید' : 'ویرایش مقاله'}
              </h1>
              <button
                type="button"
                onClick={() => setShowSEOPanel(!showSEOPanel)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
              >
                <Globe className="w-4 h-4" />
                {showSEOPanel ? 'بستن تنظیمات سئو' : 'تنظیمات سئو (پیشرفته)'}
              </button>
            </div>

            {showSEOPanel && (
              <SEOEditor
                initialData={seoData}
                onSave={handleSaveSEO}
                onCancel={() => setShowSEOPanel(false)}
                content={formData.content}
                title="تنظیمات سئوی مقاله"
                keywordsSuggestion={getKeywordSuggestions()}
              />
            )}

            {/* بخش تصویر شاخص */}
            {!isNew && (
              <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                <h2 className="text-lg font-semibold mb-4">تصویر شاخص مقاله</h2>
                <div className="flex items-center gap-4">
                  {featuredImage && (
                    <img src={featuredImage} alt="Featured" className="w-32 h-32 object-cover rounded-lg" />
                  )}
                  <label className="flex items-center justify-center w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition bg-white">
                    <div className="text-center">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <span className="text-xs text-gray-500">{uploadingImage ? 'در حال آپلود...' : 'آپلود تصویر'}</span>
                    </div>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
                  </label>
                </div>
              </div>
            )}

            {/* فرم ویرایش */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">عنوان مقاله *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  آدرس یکتا (slug)
                  {manualSlug && <span className="text-xs text-blue-600 mr-2">(دستی)</span>}
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 font-mono"
                  placeholder="مثال: مقاله-جدید-123"
                />
                <small className="text-xs text-gray-500 block mt-1">
                  آدرس نهایی: {config.SITE_URL}/blog/{formData.slug || '...'}
                </small>
                {!manualSlug && (
                  <small className="text-xs text-green-600 block mt-1">
                    💡 با تغییر عنوان، این آدرس خودکار به‌روز می‌شود
                  </small>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">خلاصه مقاله</label>
                <textarea
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="خلاصه‌ای از مقاله که در لیست بلاگ نمایش داده می‌شود..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">محتوا *</label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  rows="15"
                  className="w-full border rounded-lg px-3 py-2 font-mono text-sm"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">از HTML برای فرمت‌دهی استفاده کنید (h1, h2, p, ul, li, etc.)</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نوع صفحه</label>
                  <select
                    name="page_type"
                    value={formData.page_type}
                    onChange={handleInputChange}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="blog">مقاله وبلاگ</option>
                    <option value="normal">صفحه معمولی</option>
                    <option value="about">درباره ما</option>
                    <option value="contact">تماس با ما</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">زمان مطالعه (دقیقه)</label>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      name="read_time"
                      value={formData.read_time}
                      onChange={handleInputChange}
                      className="flex-1 border rounded-lg px-3 py-2"
                      placeholder="خالی = محاسبه خودکار"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => navigate('/admin/blog')} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  انصراف
                </button>
                <button type="submit" disabled={submitting} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
